/**
 * Générateurs des ITEMS NATIONAUX : 4e, Automatismes, campagne 2026.
 * Même contrat que 2026/6e/automatismes.js (le modèle) : un générateur par item, sa fiche en tête
 * (lue sur la CAPTURE de l'item), ITEMS avec la bonne réponse officielle exacte, CATEGORIES, generate.
 *
 * La fiche : « Item » (l'énoncé et les quatre choix de la capture, le juste en premier), « Domaine »
 * (les valeurs de l'item et le domaine qu'on en tire), « Erreurs » (l'erreur qui produit chaque
 * mauvaise réponse officielle ; la variante recalcule cette erreur sur ses propres valeurs).
 * Par construction : la bonne réponse est confirmée par un second calcul (garde lève une erreur
 * sinon), et les quatre choix sont distincts après la normalisation du correcteur (choixDistincts).
 * Choix en texte, ou en KaTeX pour une fraction, une puissance, une expression ou un calcul ; figures
 * en SVG maison dans elements_additionnels, conformes aux valeurs tirées, sans jamais la réponse.
 * Q13 a des figures pour choix : elles sont réunies dans une seule image, étiquetées A, B, C, D, et
 * les choix sont « A » à « D » (mode auto, TRANCHÉ LE 10/10 (on garde)). Q13 et Q20 gardent leurs choix dans l'ordre.
 */
import { randInt, randomChoice, shuffle, makeQcm, tex, fracTex, formatNumber } from '../../../utils';
import { normalizeAnswer } from '../../../../utils/questionUtils';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: null, label });

// ─── Outils communs ─────────────────────────────────────────────────────────

/** Le second calcul ne confirme pas la construction : la variante n'est jamais servie. */
const garde = (condition, message) => {
    if (!condition) throw new Error(`evalnat 4e automatismes, ${message}`);
};

/** Les choix d'un QCM, jamais deux égaux après la normalisation du correcteur (FORMATS_TANOED § 0.1). */
const choixDistincts = (choix, item) => {
    const vus = new Set(choix.map((c) => normalizeAnswer(String(c))));
    garde(vus.size === choix.length, `${item} : deux choix égaux après normalisation (${choix.join(' | ')})`);
    return choix;
};

const LETTRES = 'ABCDEFGH';

/** makeQcm mélange les choix ; des choix « A » à « D » ou « le point A »… se lisent dans l'ordre. */
const qcmDansLOrdre = (choix, juste, enonce, options) => {
    const payload = makeQcm(choix, juste, enonce, options);
    payload.contenu.choix = [...choix];
    payload.contenu.bonne_reponse = LETTRES[choix.indexOf(juste)];
    return payload;
};

const MOINS = '\u2212';
/** Un entier relatif avec le vrai signe moins (« −12 »), les milliers espacés. */
const relatif = (n) => (n < 0 ? `${MOINS}${formatNumber(-n)}` : formatNumber(n));

/** n ÷ 10^k en écriture décimale : decimal(75, 2) = « 0,75 », decimal(43, 1) = « 4,3 », decimal(4300) = « 4 300 ». */
const decimal = (n, k = 0) => {
    const p = 10 ** k;
    const apresVirgule = String(n % p).padStart(k, '0').replace(/0+$/, '');
    const entiere = formatNumber(Math.floor(n / p));
    return apresVirgule ? `${entiere},${apresVirgule}` : entiere;
};

const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));
const somme = (valeurs) => valeurs.reduce((s, v) => s + v, 0);
const repete = (n, valeur) => Array.from({ length: n }, () => valeur);
const rad = (degres) => (degres * Math.PI) / 180;

// ─── Figures SVG (couleurs fixes : rendu « papier », fond blanc) ────────────

const POLICE = "system-ui, -apple-system, 'Segoe UI', Arial, sans-serif";
const POLICE_MATH = "KaTeX_Math, 'Times New Roman', serif";
const ENCRE = '#1E293B';
const BLEU = '#1E3A8A';
const COULEUR = '#5468C0';
const BLANC = '#FFFFFF';
const TRAIT = `stroke="${ENCRE}" stroke-width="1.6"`;
const px = (v) => Math.round(v * 10) / 10;

const ouvrirSvg = (w, h, description) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}"`
    + ` role="img" aria-label="${description}" font-family="${POLICE}"><rect x="0" y="0" width="${w}" height="${h}" fill="${BLANC}"/>`;
const ecrire = (x, y, contenu, attributs) => `<text x="${px(x)}" y="${px(y)}" ${attributs}>${contenu}</text>`;
const segment = (P, Q, attributs = TRAIT) => `<line x1="${px(P.x)}" y1="${px(P.y)}" x2="${px(Q.x)}" y2="${px(Q.y)}" ${attributs}/>`;

/**
 * La figure de l'énoncé : SVG en data-URL dans config.imageUrl (FORMATS_TANOED § 12). Le dessin est fait
 * à la largeur de son maxWidth (small 300, medium 500, large 700) : il s'affiche à sa taille.
 */
const figure = (svg, alt, maxWidth) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, alt, maxWidth } }],
});

/**
 * Un disque partagé en secteurs : parts = [{ taille, colorie }], tailles relatives, dessinées dans le sens
 * des aiguilles d'une montre à partir de `debut` (en tours : 0 = midi, 0,25 = trois heures).
 */
const disque = (cx, cy, R, parts, debut) => {
    const total = somme(parts.map((x) => x.taille));
    const point = (t) => `${px(cx + R * Math.sin(2 * Math.PI * t))},${px(cy - R * Math.cos(2 * Math.PI * t))}`;
    let t = debut;
    let s = '';
    parts.forEach(({ taille, colorie }) => {
        const fin = t + taille / total;
        s += `<path d="M${px(cx)},${px(cy)} L${point(t)} A${R},${R} 0 ${fin - t > 0.5 ? 1 : 0} 1 ${point(fin)} Z"`
            + ` fill="${colorie ? COULEUR : BLANC}" stroke="${ENCRE}" stroke-width="1.1" stroke-linejoin="round"/>`;
        t = fin;
    });
    return `${s}<circle cx="${px(cx)}" cy="${px(cy)}" r="${R}" fill="none" ${TRAIT}/>`;
};

/** Un rectangle partagé en bandes (horizontales, de haut en bas) ou en colonnes (de gauche à droite). */
const bandes = (x, y, w, h, parts, horizontales) => {
    const total = somme(parts.map((b) => b.taille));
    let cumul = 0;
    let s = '';
    parts.forEach(({ taille, colorie }) => {
        const a = cumul / total;
        cumul += taille;
        const b = cumul / total;
        const r = horizontales ? { x, y: y + h * a, w, h: h * (b - a) } : { x: x + w * a, y, w: w * (b - a), h };
        s += `<rect x="${px(r.x)}" y="${px(r.y)}" width="${px(r.w)}" height="${px(r.h)}" fill="${colorie ? COULEUR : BLANC}" stroke="${ENCRE}" stroke-width="1.1"/>`;
    });
    return `${s}<rect x="${px(x)}" y="${px(y)}" width="${px(w)}" height="${px(h)}" fill="none" ${TRAIT}/>`;
};

// ─── Q01 ────────────────────────────────────────────────────────────────────

/**
 * Q01 : Multiplier un nombre par 0,1.
 * Item (énoncé en texte dans la base, version 2026, sans capture) : « Lorsqu'on multiplie un nombre par 0,1
 *   le nombre obtenu est… » ; 10 fois plus petit. (juste), 100 fois plus petit., 10 fois plus grand.,
 *   100 fois plus grand.
 * Domaine : le multiplicateur 0,1 de l'item, gardé, et les quatre choix de l'item, gardés. L'item parle
 *   d'« un nombre » et n'a pas d'autre valeur : tel quel, il n'a qu'une variante. La variante nomme donc
 *   le nombre multiplié (un entier de 11 à 999, jamais un multiple de 10) et, une fois sur cinq, garde
 *   « un nombre ». Adapté, TRANCHÉ LE 10/10 (on garde) (autre voie possible : multiplier par 0,01 ou 0,001).
 * Erreurs : « 100 fois plus petit » : un rang de trop, 0,1 pris pour un centième ; « 10 fois plus grand » :
 *   « multiplier agrandit », 0,1 traité comme 10 ; « 100 fois plus grand » : les deux erreurs ensemble.
 */
const L01 = 'Multiplier un nombre par 0,1';
const CHOIX_Q01 = ['10 fois plus petit.', '100 fois plus petit.', '10 fois plus grand.', '100 fois plus grand.'];
const genQ01 = () => {
    const general = Math.random() < 0.2;
    const n = 10 * randInt(1, 99) + randInt(1, 9);
    // second calcul : n × 0,1 écrit en décimal (n dixièmes), relu, comparé à n : 10 fois plus petit
    const produit = Number(decimal(n, 1).replace(',', '.'));
    garde(Math.abs(produit * 10 - n) < 1e-9 && produit < n, 'Q01, rapport');
    const sujet = general ? 'un nombre' : String(n);
    return makeQcm(choixDistincts([...CHOIX_Q01], 'Q01'), CHOIX_Q01[0],
        `<p>On multiplie ${sujet} par 0,1. Le nombre obtenu est…</p>`, opts(L01));
};

// ─── Q02 ────────────────────────────────────────────────────────────────────

/**
 * Q02 : Trouver la moitié d'un entier pair inférieur à 100.
 * Item : « Combien vaut la moitié de 70 ? » ; 35 (juste), 45, 40, 30.
 * Domaine : 70, un entier pair dont le chiffre des dizaines est impair : la moitié coupe une dizaine
 *   en deux (la moitié de 7 dizaines, c'est 3 dizaines et 5 unités). Avec 0 unité, il n'y a que 30, 50,
 *   70 et 90 : le domaine est élargi aux unités paires (74 → 37), tirées une fois sur deux ; dizaines
 *   3, 5, 7 ou 9. 20 nombres. Élargi, TRANCHÉ LE 10/10 (on garde).
 * Erreurs : la moitié de 7 dizaines arrondie au-dessus (4 dizaines) et les 5 unités gardées (45) ;
 *   arrondie au-dessus, les 5 unités oubliées (40) ; arrondie au-dessous, les 5 unités oubliées (30).
 *   Avec des unités, la moitié des unités s'ajoute partout (74 : 37, 47, 42, 32).
 */
const L02 = 'Trouver la moitié d\'un entier pair inférieur à 100';
const genQ02 = () => {
    const d = randomChoice([3, 5, 7, 9]);
    const u = Math.random() < 0.5 ? 0 : randomChoice([2, 4, 6, 8]);
    const n = 10 * d + u;
    const bas = (d - 1) / 2;
    const haut = (d + 1) / 2;
    const juste = 10 * bas + 5 + u / 2;
    garde(2 * juste === n, 'Q02, moitié');
    const choix = choixDistincts([juste, 10 * haut + 5 + u / 2, 10 * haut + u / 2, 10 * bas + u / 2].map(String), 'Q02');
    return makeQcm(choix, String(juste), `<p>Combien vaut la moitié de ${n} ?</p>`, opts(L02));
};

// ─── Q03 ────────────────────────────────────────────────────────────────────

/**
 * Q03 : Utiliser des fractions pour mesurer des grandeurs (lire une fraction).
 * Item : « Quelle fraction de la surface est coloriée ? », un disque partagé en 8 parts égales, 5 parts
 *   coloriées d'un seul tenant ; 5/8 (juste), 3/5, 3/8, 5/3.
 * Domaine : un disque partagé en n parts égales (n = 5, 7, 8, 9 ou 10), k parts coloriées d'un seul
 *   tenant, au moins 2 parts coloriées et 2 blanches, k premier avec n : les quatre fractions sont
 *   irréductibles, comme dans l'item. 14 partages.
 * Erreurs : la partie comparée à l'autre partie au lieu du tout : parts blanches sur parts coloriées
 *   (3/5) et parts coloriées sur parts blanches (5/3) ; la fraction de la partie blanche (3/8).
 */
const L03 = 'Fractions et grandeurs : lire la part coloriée';
const PARTAGES_Q03 = [];
[5, 7, 8, 9, 10].forEach((n) => {
    for (let k = 2; k <= n - 2; k += 1) {
        if (pgcd(k, n) === 1) PARTAGES_Q03.push([n, k]);
    }
});
const genQ03 = () => {
    const [n, k] = randomChoice(PARTAGES_Q03);
    const parts = Array.from({ length: n }, (_, i) => ({ taille: 1, colorie: i < k }));
    // second calcul : la part coloriée mesurée sur les parts dessinées
    const coloriees = parts.filter((x) => x.colorie).length;
    const blanches = parts.length - coloriees;
    garde(coloriees === k && blanches >= 2 && coloriees >= 2 && pgcd(coloriees, parts.length) === 1, 'Q03, partage');
    const svg = `${ouvrirSvg(300, 196, 'Un disque partagé en parts égales, dont certaines sont coloriées')}${disque(150, 98, 86, parts, 0.25)}</svg>`;
    const juste = fracTex(coloriees, n);
    const choix = choixDistincts([juste, fracTex(blanches, coloriees), fracTex(blanches, n), fracTex(coloriees, blanches)], 'Q03');
    return makeQcm(choix, juste, '<p>Quelle fraction de la surface est coloriée ?</p>',
        { ...opts(L03), elements_additionnels: figure(svg, 'Un disque partagé en parts égales, dont certaines sont coloriées.', 'small') });
};

// ─── Q04 ────────────────────────────────────────────────────────────────────

/**
 * Q04 : Additionner des nombres entiers.
 * Item : « 168 + 18 = … » ; 186 (juste), 190, 188, 176.
 * Domaine : un nombre de trois chiffres plus un nombre de 11 à 29, qui finissent par le même chiffre,
 *   7, 8 ou 9 : une retenue aux unités, aucune aux dizaines, deux termes proches de la dizaine du
 *   dessus (168 ≈ 170, 18 ≈ 20) ; la somme reste sous 1 000.
 * Erreurs : les deux termes arrondis à la dizaine du dessus sans compenser (170 + 20 = 190) ; un seul
 *   terme arrondi sans compenser (168 + 20 = 188, ou 170 + 18 : même chiffre des unités, même nombre) ;
 *   la retenue des unités oubliée (176).
 */
const L04 = 'Additionner des nombres entiers';
const genQ04 = () => {
    const u = randomChoice([7, 8, 9]);
    const dizB = randomChoice([1, 2]);
    const dizA = randInt(0, 8 - dizB);
    const cent = randInt(1, 8);
    const a = 100 * cent + 10 * dizA + u;
    const b = 10 * dizB + u;
    const juste = a + b;
    // second calcul : l'addition posée, colonne par colonne
    const retenue = Math.floor((u + u) / 10);
    const dizaines = dizA + dizB + retenue;
    garde(retenue === 1 && dizaines <= 9 && 100 * cent + 10 * dizaines + ((u + u) % 10) === juste, 'Q04, addition posée');
    const dizaineDuDessus = (x) => x - (x % 10) + 10;
    const choix = choixDistincts([juste, dizaineDuDessus(a) + dizaineDuDessus(b), a + dizaineDuDessus(b), juste - 10].map(String), 'Q04');
    return makeQcm(choix, String(juste), `<p>${tex(`${a} + ${b} = \\dots`)}</p>`, opts(L04));
};

// ─── Q05 ────────────────────────────────────────────────────────────────────

/**
 * Q05 : Additionner des nombres entiers relatifs.
 * Item : « −5 + 7 = … » ; 2 (juste), −12, 12, −2.
 * Domaine : −a + b, a et b entiers de 2 à 9, a < b : un négatif plus un positif plus loin de zéro,
 *   le résultat est positif. 28 couples.
 * Erreurs : les distances à zéro ajoutées et le signe moins gardé (−12) ; les distances ajoutées, les
 *   signes oubliés (12) ; la bonne différence des distances avec le signe du premier terme (−2).
 */
const L05 = 'Additionner des nombres entiers relatifs';
const genQ05 = () => {
    const a = randInt(2, 8);
    const b = randInt(a + 1, 9);
    const juste = -a + b;
    garde(juste === b - a && juste > 0, 'Q05, somme');
    const choix = choixDistincts([relatif(juste), relatif(-(a + b)), relatif(a + b), relatif(-(b - a))], 'Q05');
    return makeQcm(choix, relatif(juste), `<p>${tex(`{-${a}} + ${b} = \\dots`)}</p>`, opts(L05));
};

// ─── Q06 ────────────────────────────────────────────────────────────────────

/**
 * Q06 : Réduire une expression littérale.
 * Item (énoncé en texte dans la base, version 2026, sans capture) : « a × a + a × 3 + 7 = … » ;
 *   a² + 3a + 7 (juste), a² + 10a, 2a + a³ + 7, 11a.
 * Domaine : a × a + a × k + c, k de 3 à 9 (k = 2 rendrait « 2a + a² + c » égal à la bonne réponse),
 *   c de 2 à 9, la lettre a et l'ordre « a × k » de l'item. 56 variantes.
 * Erreurs : le nombre c ajouté aux a (3a + 7 = 10a, d'où a² + 10a) ; a × a lu a + a et a × 3 lu a³
 *   (2a + a³ + 7) ; tout réduit en un seul terme en a (a × a lu a, a × 3 lu 3a, 7 lu 7a : 11a).
 */
const L06 = 'Réduire une expression littérale';
const genQ06 = () => {
    const k = randInt(3, 9);
    const c = randInt(2, 9);
    // second calcul : l'énoncé et la réponse prennent la même valeur pour plusieurs valeurs de a,
    // et chaque mauvaise réponse en diffère pour au moins une
    const enonce = (a) => a * a + a * k + c;
    const formes = [(a) => a ** 2 + k * a + c, (a) => a ** 2 + (k + c) * a, (a) => 2 * a + a ** k + c, (a) => (1 + k + c) * a];
    const essais = [2, 3, 5];
    garde(essais.every((a) => formes[0](a) === enonce(a)), 'Q06, réduction');
    garde(formes.slice(1).every((f) => essais.some((a) => f(a) !== enonce(a))), 'Q06, distracteur égal à l\'énoncé');
    const juste = tex(`a^2 + ${k}a + ${c}`);
    const choix = choixDistincts([juste, tex(`a^2 + ${k + c}a`), tex(`2a + a^${k} + ${c}`), tex(`${1 + k + c}a`)], 'Q06');
    return makeQcm(choix, juste, `<p>${tex(`a \\times a + a \\times ${k} + ${c} = \\dots`)}</p>`, opts(L06));
};

// ─── Q07 ────────────────────────────────────────────────────────────────────

/**
 * Q07 : Convertir des durées en heures et minutes.
 * Item : « Max assiste à un spectacle qui dure 135 minutes. Comment cette durée peut-elle s'écrire
 *   autrement ? » ; 2h 15min (juste), 1h 35min, 1h 15min, 2h 35min.
 * Domaine : une durée de 121 à 159 minutes, soit 2 h et 1 à 39 min ; lue « à la décimale », elle donne
 *   1 h et 21 à 59 min, toujours moins de 60 min, et les quatre durées sont distinctes. Le prénom change.
 *   Les choix sont écrits « 2 h 15 min ».
 * Erreurs : la durée lue comme un nombre décimal, 1 h = 100 min (1 h 35 min) ; les heures lues sur le
 *   chiffre des centaines, les minutes justes (1 h 15 min) ; les heures justes, les minutes lues sur les
 *   deux derniers chiffres (2 h 35 min).
 */
const L07 = 'Convertir des durées en heures et minutes';
const PRENOMS_Q07 = ['Max', 'Léa', 'Tom', 'Inès', 'Noah', 'Jade', 'Sami', 'Lina', 'Enzo', 'Maya'];
const duree = (h, m) => `${h} h ${m} min`;
const genQ07 = () => {
    const n = randInt(121, 159);
    const h = Math.floor(n / 60);
    const m = n % 60;
    const hLu = Math.floor(n / 100);
    const mLu = n % 100;
    garde(60 * h + m === n && h === 2 && m > 0 && hLu === 1 && mLu < 60, 'Q07, conversion');
    const juste = duree(h, m);
    const choix = choixDistincts([juste, duree(hLu, mLu), duree(hLu, m), duree(h, mLu)], 'Q07');
    return makeQcm(choix, juste, `<p>${randomChoice(PRENOMS_Q07)} assiste à un spectacle qui dure ${n} minutes.`
        + ' Comment cette durée peut-elle s\'écrire autrement ?</p>', opts(L07));
};

// ─── Q08 ────────────────────────────────────────────────────────────────────

/**
 * Q08 : Factoriser une expression numérique (calcul mental).
 * Item : « 12 × 7 + 12 × 3 = … » ; 120 (juste), 240, 36, 84.
 * Domaine : k × a + k × b, k de 11 à 19, a + b = 10 et a > b (8 et 2, 7 et 3, 6 et 4) : la factorisation
 *   donne k × 10. 27 variantes.
 * Erreurs : le facteur commun compté deux fois, (12 + 12) × (7 + 3) = 240 ; un seul des deux produits,
 *   le second (12 × 3 = 36) ou le premier (12 × 7 = 84).
 */
const L08 = 'Factoriser une expression numérique (calcul mental)';
const genQ08 = () => {
    const k = randInt(11, 19);
    const [a, b] = randomChoice([[8, 2], [7, 3], [6, 4]]);
    const juste = k * a + k * b;
    garde(juste === k * (a + b) && a + b === 10, 'Q08, factorisation');
    const choix = choixDistincts([juste, (k + k) * (a + b), k * b, k * a].map(String), 'Q08');
    return makeQcm(choix, String(juste), `<p>${tex(`${k} \\times ${a} + ${k} \\times ${b} = \\dots`)}</p>`, opts(L08));
};

// ─── Q09 ────────────────────────────────────────────────────────────────────

/**
 * Q09 : Calculer un angle dans un triangle connaissant les deux autres.
 * Item : « La mesure de l'angle IJK est égale à … », triangle IJK, 50° marqué en I, 20° en K ;
 *   110° (juste), 20°, 50°, 70°.
 * Domaine : les deux angles connus en dizaines de degrés, de 20° à 60°, différents, de somme 80° au
 *   plus : l'angle en J est obtus, comme dans l'item. 12 couples. La figure est construite à partir des
 *   deux angles, l'angle en J n'y est pas marqué.
 * Erreurs : un angle donné recopié (20° ou 50°) ; les deux angles donnés ajoutés, sans les retrancher
 *   de 180° (70°).
 */
const L09 = 'Calculer un angle d\'un triangle connaissant les deux autres';
const ANGLES_Q09 = [];
for (let a = 20; a <= 60; a += 10) {
    for (let g = 20; g <= 60; g += 10) {
        if (a !== g && a + g <= 80) ANGLES_Q09.push([a, g]);
    }
}
/** Le triangle IJK construit à partir des angles en I et en K ; rend aussi l'angle en J mesuré sur le dessin. */
const triangleIJK = (a, g) => {
    const L = 230;
    const IJ = (L * Math.sin(rad(g))) / Math.sin(rad(a + g));
    const hJ = IJ * Math.sin(rad(a));
    const oy = 36 + hJ;
    const I = { x: 30, y: oy };
    const K = { x: 30 + L, y: oy };
    const J = { x: 30 + IJ * Math.cos(rad(a)), y: oy - hJ };
    const u = { x: I.x - J.x, y: I.y - J.y };
    const v = { x: K.x - J.x, y: K.y - J.y };
    const angleJ = (Math.acos((u.x * v.x + u.y * v.y) / (Math.hypot(u.x, u.y) * Math.hypot(v.x, v.y))) * 180) / Math.PI;
    const arc = (P, r, t0, t1) => `<path d="M${px(P.x + r * Math.cos(rad(t0)))},${px(P.y - r * Math.sin(rad(t0)))}`
        + ` A${r},${r} 0 0 0 ${px(P.x + r * Math.cos(rad(t1)))},${px(P.y - r * Math.sin(rad(t1)))}" fill="none" stroke="${ENCRE}" stroke-width="1.2"/>`;
    // la mesure, sur la bissectrice, assez loin du sommet pour tenir entre les deux côtés
    const etiquette = (P, direction, mesure) => {
        const r = Math.max(40, 10 / Math.sin(rad(mesure / 2)) + 8);
        return ecrire(P.x + r * Math.cos(rad(direction)), P.y - r * Math.sin(rad(direction)), `${mesure}°`,
            `text-anchor="middle" dy=".35em" font-size="14" fill="${ENCRE}"`);
    };
    const sommet = `font-size="16" font-weight="600" fill="${ENCRE}"`;
    let s = ouvrirSvg(300, px(oy + 28), 'Triangle IJK avec deux angles marqués');
    s += `<polygon points="${px(I.x)},${px(I.y)} ${px(J.x)},${px(J.y)} ${px(K.x)},${px(K.y)}" fill="none" ${TRAIT} stroke-linejoin="round"/>`;
    s += arc(I, 24, 0, a) + etiquette(I, a / 2, a);
    s += arc(K, 22, 180 - g, 180) + arc(K, 27, 180 - g, 180) + etiquette(K, 180 - g / 2, g);
    s += ecrire(I.x - 4, I.y + 18, 'I', `text-anchor="end" ${sommet}`);
    s += ecrire(K.x + 8, K.y + 6, 'K', sommet);
    s += ecrire(J.x, J.y - 9, 'J', `text-anchor="middle" ${sommet}`);
    return { svg: `${s}</svg>`, angleJ };
};
const genQ09 = () => {
    const [a, g] = randomChoice(ANGLES_Q09);
    const juste = 180 - a - g;
    const { svg, angleJ } = triangleIJK(a, g);
    // second calcul : l'angle en J mesuré sur la figure construite
    garde(Math.abs(angleJ - juste) < 0.5 && juste > 90, 'Q09, angle en J');
    const deg = (x) => `${x}°`;
    const choix = choixDistincts([deg(juste), deg(g), deg(a), deg(a + g)], 'Q09');
    return makeQcm(choix, deg(juste), `<p>La mesure de l'angle ${tex('\\widehat{IJK}')} est égale à …</p>`,
        { ...opts(L09), elements_additionnels: figure(svg, 'Triangle IJK avec deux angles marqués.', 'small') });
};

// ─── Q10 ────────────────────────────────────────────────────────────────────

/**
 * Q10 : Associer différentes écritures d'un nombre décimal.
 * Item : « 14 + 6/10 + 2/1000 = … » ; 14,602 (juste), 1 462, 14,62, 140,62.
 * Domaine : n + t/10 + m/1000, n de 11 à 99 (jamais un multiple de 10), t et m de 1 à 9 : des dixièmes,
 *   pas de centièmes, des millièmes, comme l'item.
 * Erreurs : les chiffres mis bout à bout sans virgule (1 462) ; le zéro des centièmes oublié (14,62) ;
 *   140,62 : un zéro écrit, mais dans la partie entière, et les chiffres collés derrière la virgule
 *   (hypothèse : 6/10 écrit 0,6 et collé derrière 14). Erreur non identifiée avec certitude, TRANCHÉ LE 10/10 (on garde) ;
 *   la variante reproduit sa forme : n suivi d'un 0, la virgule, puis t et m.
 */
const L10 = 'Associer différentes écritures d\'un nombre décimal';
const genQ10 = () => {
    const n = 10 * randInt(1, 9) + randInt(1, 9);
    const t = randInt(1, 9);
    const m = randInt(1, 9);
    const juste = `${n},${t}0${m}`;
    // second calcul : la somme en millièmes, réécrite en décimal
    garde(decimal(1000 * n + 100 * t + m, 3) === juste, 'Q10, écriture décimale');
    const choix = choixDistincts([juste, formatNumber(Number(`${n}${t}${m}`)), `${n},${t}${m}`, `${n}0,${t}${m}`], 'Q10');
    return makeQcm(choix, juste, `<p>${tex(`${n} + \\dfrac{${t}}{10} + \\dfrac{${m}}{1000} = \\dots`)}</p>`, opts(L10));
};

// ─── Q11 ────────────────────────────────────────────────────────────────────

/**
 * Q11 : Calculer une distance à partir d'une vitesse et d'une durée.
 * Item : « Un cycliste roule pendant 30 min à la vitesse moyenne de 18 km/h. Quelle distance
 *   parcourt-il ? » ; 9 km (juste), 18 km, 36 km, 5,4 km.
 * Domaine : 30 min gardées, une vitesse paire de 12 à 38 km/h, jamais un multiple de 10 (sinon 0,3 × v
 *   serait un entier, alors que 5,4 est décimal). 12 vitesses.
 * Erreurs : la vitesse prise pour la distance, la durée oubliée (18 km) ; multiplié par 2 au lieu de
 *   diviser par 2 (36 km) ; 30 min lues 0,3 h (18 × 0,3 = 5,4 km).
 */
const L11 = 'Calculer une distance à partir d\'une vitesse et d\'une durée';
const VITESSES_Q11 = [12, 14, 16, 18, 22, 24, 26, 28, 32, 34, 36, 38];
const genQ11 = () => {
    const v = randomChoice(VITESSES_Q11);
    // second calcul : v km en 60 min, donc v × 30 ÷ 60 km en 30 min
    const juste = (v * 30) / 60;
    garde(Number.isInteger(juste) && juste * 2 === v && (3 * v) % 10 !== 0, 'Q11, distance');
    const km = (x) => `${x} km`;
    const choix = choixDistincts([km(juste), km(v), km(2 * v), km(decimal(3 * v, 1))], 'Q11');
    return makeQcm(choix, km(juste), `<p>Un cycliste roule pendant 30 min à la vitesse moyenne de ${v} km/h.</p>`
        + '<p>Quelle distance parcourt-il ?</p>', opts(L11));
};

// ─── Q12 ────────────────────────────────────────────────────────────────────

/**
 * Q12 : Unités de numération décimale.
 * Item : « Compléter par le nombre qui convient : 43 milliers = … dizaines » ; 4 300 (juste), 4,3, 43, 430.
 * Domaine : n milliers = … dizaines, n de 11 à 99, jamais un multiple de 10 (4,3 reste décimal).
 * Erreurs : la conversion dans le mauvais sens, divisé par 10 (4,3) ; le nombre recopié (43) ; un millier
 *   pris pour 10 dizaines, multiplié par 10 au lieu de 100 (430).
 */
const L12 = 'Unités de numération décimale';
const genQ12 = () => {
    const n = 10 * randInt(1, 9) + randInt(1, 9);
    const dizaines = 100 * n;
    // second calcul : n milliers = n × 1 000 unités = (n × 1 000) ÷ 10 dizaines
    garde((n * 1000) / 10 === dizaines, 'Q12, conversion');
    const juste = formatNumber(dizaines);
    const choix = choixDistincts([juste, decimal(n, 1), String(n), formatNumber(10 * n)], 'Q12');
    return makeQcm(choix, juste, `<p>Compléter par le nombre qui convient :</p><p>${n} milliers = … dizaines</p>`, opts(L12));
};

// ─── Q13 ────────────────────────────────────────────────────────────────────

/**
 * Q13 : Utiliser des fractions pour mesurer des grandeurs (reconnaître une fraction).
 * Item : « Dans quelle figure a-t-on colorié 3/4 de la surface ? », quatre figures pour choix : Image DD
 *   (juste : disque en 8 parts égales, 6 coloriées), Image GG (disque en 4 parts inégales, 1/2, 1/4 et 1/8
 *   coloriés, 1/8 blanc), Image D (rectangle en 4 bandes horizontales inégales, les 3 du haut coloriées),
 *   Image G (rectangle en 3 colonnes étroites coloriées et une large colonne blanche).
 * Domaine : la fraction p/q, irréductible, 2 ≤ p < q ≤ 5 (2/3, 3/4, 2/5, 3/5, 4/5) ; la bonne figure est
 *   un disque en kq parts égales, kp coloriées, k = 2 ou 3. 10 variantes, figures placées au hasard.
 * Erreurs : les trois autres figures montrent p parts coloriées sur q parts, mais des parts inégales :
 *   disque partagé par moitiés successives (GG), bandes dont une double (D), colonnes étroites et
 *   colonnes blanches larges (G). L'erreur : compter les parts sans vérifier qu'elles sont égales ; la
 *   bonne figure, elle, ne montre pas « p sur q » mais kp sur kq.
 * Mode auto, TRANCHÉ LE 10/10 (on garde) : les choix officiels sont des figures. Les quatre figures sont réunies dans une
 *   seule image, étiquetées A, B, C, D, et les choix sont « A », « B », « C », « D », dans l'ordre.
 */
const L13 = 'Fractions et grandeurs : trouver la figure coloriée';
const FRACTIONS_Q13 = [[2, 3], [3, 4], [2, 5], [3, 5], [4, 5]];
const svgQ13 = (figures) => {
    let s = ouvrirSvg(500, 440, 'Quatre figures A, B, C et D, coloriées en partie');
    figures.forEach((f, i) => {
        const x = (i % 2) * 250;
        const y = Math.floor(i / 2) * 220;
        s += ecrire(x + 125, y + 30, LETTRES[i], `text-anchor="middle" font-size="22" font-weight="700" fill="${BLEU}"`);
        s += f.dessiner(x + 125, y + 128, f.parts);
    });
    return `${s}</svg>`;
};
const genQ13 = () => {
    const [p, q] = randomChoice(FRACTIONS_Q13);
    const k = randomChoice([2, 3]);
    const moities = [1, ...Array.from({ length: q - 1 }, (_, i) => 2 ** i)];
    const figures = [
        {
            nom: 'juste',
            parts: Array.from({ length: k * q }, (_, i) => ({ taille: 1, colorie: i < k * p })),
            dessiner: (cx, cy, parts) => disque(cx, cy, 78, parts, 0.25),
        },
        {
            nom: 'disque_par_moities',
            parts: moities.map((taille, i) => ({ taille, colorie: i >= q - p })),
            dessiner: (cx, cy, parts) => disque(cx, cy, 78, parts, 0),
        },
        {
            nom: 'bandes_inegales',
            parts: [...repete(p - 1, 1), 2, ...repete(q - p, 1)].map((taille, i) => ({ taille, colorie: i < p })),
            dessiner: (cx, cy, parts) => bandes(cx - 80, cy - 68, 160, 136, parts, true),
        },
        {
            nom: 'colonnes_inegales',
            parts: [...repete(p, 1), ...repete(q - p, p)].map((taille, i) => ({ taille, colorie: i < p })),
            dessiner: (cx, cy, parts) => bandes(cx - 110, cy - 40, 220, 80, parts, false),
        },
    ];
    // second calcul : la part coloriée de chaque figure, mesurée sur ses parts. Seule la bonne figure vaut
    // p/q avec des parts égales ; les trois autres montrent p parts coloriées sur q parts inégales.
    figures.forEach((f) => {
        const coloriee = somme(f.parts.filter((x) => x.colorie).map((x) => x.taille));
        const total = somme(f.parts.map((x) => x.taille));
        const vautPq = coloriee * q === p * total;
        const egales = f.parts.every((x) => x.taille === f.parts[0].taille);
        const pSurQ = f.parts.length === q && f.parts.filter((x) => x.colorie).length === p;
        garde(f.nom === 'juste' ? (vautPq && egales) : (!vautPq && !egales && pSurQ), `Q13, figure ${f.nom}`);
    });
    const ordre = shuffle(figures);
    const juste = LETTRES[ordre.findIndex((f) => f.nom === 'juste')];
    return qcmDansLOrdre(['A', 'B', 'C', 'D'], juste, `<p>Dans quelle figure a-t-on colorié ${fracTex(p, q)} de la surface ?</p>`,
        { ...opts(L13), elements_additionnels: figure(svgQ13(ordre), 'Quatre figures A, B, C et D, coloriées en partie.', 'medium') });
};

// ─── Q14 ────────────────────────────────────────────────────────────────────

/**
 * Q14 : Soustraire deux nombres relatifs.
 * Item : « 7 − (−5) = … » ; 12 (juste), −12, 2, −2.
 * Domaine : a − (−b), a et b entiers de 2 à 9, b < a, comme 5 < 7. 28 couples.
 * Erreurs : la bonne somme des distances avec le signe moins, recopié des signes de l'énoncé (−12) ;
 *   la soustraction de −b traitée comme celle de b (7 − 5 = 2) ; les deux erreurs ensemble (−2).
 */
const L14 = 'Soustraire deux nombres relatifs';
const genQ14 = () => {
    const a = randInt(3, 9);
    const b = randInt(2, a - 1);
    // second calcul : soustraire −b, c'est ajouter son opposé b
    const juste = a - (-b);
    garde(juste === a + b, 'Q14, soustraction');
    const choix = choixDistincts([relatif(juste), relatif(-(a + b)), relatif(a - b), relatif(-(a - b))], 'Q14');
    return makeQcm(choix, relatif(juste), `<p>${tex(`${a} - (-${b}) = \\dots`)}</p>`, opts(L14));
};

// ─── Q15 ────────────────────────────────────────────────────────────────────

/**
 * Q15 : Placer et repérer une fraction sur une droite graduée.
 * Item : « Quelle est l'abscisse du point A ? », droite graduée de 0 à 3, chaque unité partagée en 4,
 *   A sur la 3e graduation après 0 ; 3/4 (juste), 0,3, 4/3, 3.
 * Domaine : la droite de 0 à 3, l'unité partagée en d (de 3 à 8), A sur la p-ième graduation après 0,
 *   2 ≤ p < d, p/d irréductible (d = 10 exclu : 0,p y serait juste). 14 variantes.
 * Erreurs : les graduations comptées comme des dixièmes (0,3) ; numérateur et dénominateur inversés (4/3) ;
 *   le nombre de graduations donné pour l'abscisse (3).
 */
const L15 = 'Repérer une fraction sur une droite graduée';
const GRADUATIONS_Q15 = [];
[3, 4, 5, 6, 7, 8].forEach((d) => {
    for (let p = 2; p < d; p += 1) {
        if (pgcd(p, d) === 1) GRADUATIONS_Q15.push([d, p]);
    }
});
/** La droite graduée de 0 à 3, l'unité en d parts, A au-dessus de la graduation p/d ; rend l'abscisse lue sur le dessin. */
const droiteQ15 = (d, p) => {
    const U = 200;
    const x0 = 40;
    const y = 48;
    const X = (v) => x0 + v * U;
    let s = ouvrirSvg(700, 92, 'Droite graduée de 0 à 3, un point A au-dessus d\'une graduation');
    s += segment({ x: X(-0.1), y }, { x: X(3) + 36, y });
    s += `<polygon points="${px(X(3) + 46)},${y} ${px(X(3) + 34)},${y - 5} ${px(X(3) + 34)},${y + 5}" fill="${ENCRE}"/>`;
    for (let i = 0; i <= 3 * d; i += 1) {
        s += segment({ x: X(i / d), y: y - 7 }, { x: X(i / d), y: y + 7 }, `stroke="${ENCRE}" stroke-width="1.5"`);
    }
    for (let j = 0; j <= 3; j += 1) {
        s += ecrire(X(j), y + 30, String(j), `text-anchor="middle" font-size="17" font-weight="700" fill="${ENCRE}"`);
    }
    const xA = X(p / d);
    s += ecrire(xA, y - 16, 'A', `text-anchor="middle" font-size="18" font-weight="700" fill="${BLEU}"`);
    return { svg: `${s}</svg>`, abscisse: (xA - x0) / U };
};
const genQ15 = () => {
    const [d, p] = randomChoice(GRADUATIONS_Q15);
    const { svg, abscisse } = droiteQ15(d, p);
    // second calcul : l'abscisse relue sur le dessin
    garde(Math.abs(abscisse - p / d) < 1e-9 && p < d && pgcd(p, d) === 1, 'Q15, abscisse');
    const juste = fracTex(p, d);
    const choix = choixDistincts([juste, `0,${p}`, fracTex(d, p), String(p)], 'Q15');
    return makeQcm(choix, juste, '<p>Quelle est l\'abscisse du point A ?</p>',
        { ...opts(L15), elements_additionnels: figure(svg, 'Droite graduée de 0 à 3 avec un point A.', 'large') });
};

// ─── Q16 ────────────────────────────────────────────────────────────────────

/**
 * Q16 : Passer d'une écriture d'un nombre décimal à une autre.
 * Item : « 0,3 s'écrit aussi … » ; 3/10 (juste), 1/3, 3/100, 0/3.
 * Domaine : 0,t, t de 2 à 9 : 8 nombres seulement, sous le minimum de 10 variantes. Le domaine est
 *   élargi, une fois sur trois, aux nombres à deux décimales 0,tu (t et u de 1 à 9 : 0,47 = 47/100), avec
 *   les mêmes erreurs. Élargi, TRANCHÉ LE 10/10 (on garde).
 * Erreurs : les chiffres après la virgule pris pour le dénominateur (0,3 lu « un tiers » : 1/3 ;
 *   0,47 : 1/47) ; un rang de trop (3/100 ; 47/1000) ; la virgule lue comme une barre de fraction (0/3).
 */
const L16 = 'Passer de l\'écriture décimale à une fraction';
const genQ16 = () => {
    const deuxDecimales = Math.random() < 1 / 3;
    const n = deuxDecimales ? 10 * randInt(1, 9) + randInt(1, 9) : randInt(2, 9);
    const den = deuxDecimales ? 100 : 10;
    const ecrit = `0,${n}`;
    // second calcul : n ÷ den réécrit en décimal
    garde(decimal(n, deuxDecimales ? 2 : 1) === ecrit, 'Q16, écriture décimale');
    const juste = fracTex(n, den);
    const choix = choixDistincts([juste, fracTex(1, n), fracTex(n, 10 * den), fracTex(0, n)], 'Q16');
    return makeQcm(choix, juste, `<p>${ecrit} s'écrit aussi …</p>`, opts(L16));
};

// ─── Q17 ────────────────────────────────────────────────────────────────────

/**
 * Q17 : Calcul littéral, substituer une valeur dans une expression.
 * Item : « On donne l'expression A = 1 + 3x. Quelle est la valeur de A pour x = 8 ? » ;
 *   25 (juste), 32, 39, 48.
 * Domaine : A = a + bx pour x = v, a de 1 à 5, b de 2 à 9 − a (a + b reste un chiffre), v de 2 à 9 ;
 *   le seul couple de choix égaux possible, (a + b) × v = a + « bv » (a = 2, b = 3, v = 8), est écarté.
 * Erreurs : l'addition faite avant la multiplication, (1 + 3) × 8 = 32 ; 3x lu comme le nombre 38,
 *   1 + 38 = 39 ; les deux ensemble, 1 + 3 = 4 accolé à 8 : 48.
 */
const L17 = 'Substituer une valeur dans une expression littérale';
const genQ17 = () => {
    let tirage = [1, 3, 8];
    for (let essai = 0; essai < 40; essai += 1) {
        const a = randInt(1, 5);
        const b = randInt(2, 9 - a);
        const v = randInt(2, 9);
        if ((a + b - 1) * v !== a + 10 * b) {
            tirage = [a, b, v];
            break;
        }
    }
    const [a, b, v] = tirage;
    const juste = a + b * v;
    // second calcul : remonter de A à x
    garde((juste - a) / b === v, 'Q17, substitution');
    const choix = choixDistincts([juste, (a + b) * v, a + Number(`${b}${v}`), Number(`${a + b}${v}`)].map(String), 'Q17');
    return makeQcm(choix, String(juste), `<p>On donne l'expression ${tex(`A = ${a} + ${b}x`)}.</p>`
        + `<p>Quelle est la valeur de ${tex('A')} pour ${tex(`x = ${v}`)} ?</p>`, opts(L17));
};

// ─── Q18 ────────────────────────────────────────────────────────────────────

/**
 * Q18 : Convertir une capacité d'une unité dans une autre.
 * Item : « Compléter l'égalité. 75 L = … cL » ; 7 500 (juste), 0,75, 7,5, 750.
 * Domaine : n L = … cL, n de 11 à 99, jamais un multiple de 10 (0,75 et 7,5 restent décimaux).
 * Erreurs : le mauvais sens, divisé par 100 (0,75) ; le mauvais sens et le mauvais rang, divisé par 10
 *   (7,5) ; le bon sens, un rang de moins, les cL pris pour des dL (750).
 */
const L18 = 'Convertir une capacité';
const genQ18 = () => {
    const n = 10 * randInt(1, 9) + randInt(1, 9);
    // second calcul : 1 L = 10 dL et 1 dL = 10 cL
    const cL = n * 10 * 10;
    garde(cL === 100 * n, 'Q18, conversion');
    const juste = formatNumber(cL);
    const choix = choixDistincts([juste, decimal(n, 2), decimal(n, 1), formatNumber(10 * n)], 'Q18');
    return makeQcm(choix, juste, `<p>Compléter l'égalité.</p><p>${n} L = … cL</p>`, opts(L18));
};

// ─── Q19 ────────────────────────────────────────────────────────────────────

/**
 * Q19 : Compléter un tableau de proportionnalité.
 * Item : « Le tableau suivant est un tableau de proportionnalité : » tableau 12 | 4 sur la première ligne,
 *   case vide | 9 sur la seconde, « Quel nombre doit-on placer dans la case vide ? » ; 27 (juste), 17, 30, 3.
 * Domaine : première ligne a | b avec a = k × b, seconde ligne case vide | c ; k de 3 à 5, b et c de 2 à 9,
 *   c > b, c pas multiple de b (le coefficient d'une ligne à l'autre n'est pas entier, comme 9/4), k
 *   différent de b et de c, a ≤ 45, k × c pas multiple de 10.
 * Erreurs : le modèle additif, la différence gardée (12 − 4 = 8, 9 + 8 = 17) ; le coefficient 3 (12 = 3 × 4)
 *   donné à la place du nombre cherché (dans l'item, le produit en croix à l'envers 4 × 9 ÷ 12 donne aussi 3 ;
 *   la variante garde le coefficient, toujours entier) ; 30 : erreur non identifiée, TRANCHÉ LE 10/10 (on garde) ; c'est la
 *   dizaine au-dessus de 27, la variante prend la dizaine au-dessus de sa bonne réponse.
 */
const L19 = 'Compléter un tableau de proportionnalité';
const tableauQ19 = (a, b, c) => {
    const w = 84;
    const h = 50;
    const x0 = 66;
    const y0 = 8;
    const cellule = (ligne, colonne, valeur) => ecrire(x0 + w * colonne + w / 2, y0 + h * ligne + h / 2, valeur,
        `text-anchor="middle" dy=".35em" font-size="22" fill="${ENCRE}"`);
    let s = ouvrirSvg(300, 2 * h + 16, 'Tableau de deux lignes et deux colonnes, avec une case vide');
    s += `<rect x="${x0}" y="${y0}" width="${2 * w}" height="${2 * h}" fill="none" ${TRAIT}/>`;
    s += segment({ x: x0 + w, y: y0 }, { x: x0 + w, y: y0 + 2 * h });
    s += segment({ x: x0, y: y0 + h }, { x: x0 + 2 * w, y: y0 + h });
    s += cellule(0, 0, a) + cellule(0, 1, b) + cellule(1, 1, c);
    return `${s}</svg>`;
};
const choixQ19 = ({ k, b, c }) => {
    const juste = k * c;
    return [juste, k * b + c - b, Math.ceil(juste / 10) * 10, k].map(String);
};
const genQ19 = () => {
    let tirage = { k: 3, b: 4, c: 9 };
    for (let essai = 0; essai < 60; essai += 1) {
        const t = { k: randInt(3, 5), b: randInt(2, 8) };
        t.c = randInt(t.b + 1, 9);
        const valide = t.c % t.b !== 0 && t.k !== t.b && t.k !== t.c && t.k * t.b <= 45 && (t.k * t.c) % 10 !== 0;
        if (valide && new Set(choixQ19(t)).size === 4) {
            tirage = t;
            break;
        }
    }
    const { k, b, c } = tirage;
    const a = k * b;
    const juste = k * c;
    // second calcul : le produit en croix, case × b = a × c
    garde(juste * b === a * c && juste % 10 !== 0, 'Q19, proportionnalité');
    const choix = choixDistincts(choixQ19(tirage), 'Q19');
    const enonce = '<p>Le tableau suivant est un tableau de proportionnalité :</p>{{element:0}}'
        + '<p>Quel nombre doit-on placer dans la case vide ?</p>';
    return makeQcm(choix, String(juste), enonce,
        { ...opts(L19), elements_additionnels: figure(tableauQ19(a, b, c), 'Tableau de deux lignes et deux colonnes, avec une case vide.', 'small') });
};

// ─── Q20 ────────────────────────────────────────────────────────────────────

/**
 * Q20 : Repérer un point dans un repère orthonormé.
 * Item : « On considère quatre points A, B, C et D dans le plan rapporté au repère ci-contre : Quel point a
 *   pour coordonnées (3 ; 5) ? », repère de 0 à 5 (de −1 à 5 en ordonnée), A(5 ; 3), B(3,5 ; 0), C(0 ; 3,5),
 *   D(3 ; 5) ; le point D (juste), le point A, le point B, le point C.
 * Domaine : (x ; y), y de 3 à 5 (lu « x,y », le nombre décimal tombe entre deux graduations, loin d'elles),
 *   x de 1 à 5, x ≠ y ; les lettres des quatre points sont tirées au hasard, les choix restent dans l'ordre.
 *   12 couples.
 * Erreurs : abscisse et ordonnée échangées (A(5 ; 3)) ; le couple (3 ; 5) lu comme le nombre 3,5 et placé
 *   sur l'axe des abscisses (B) ou sur l'axe des ordonnées (C).
 */
const L20 = 'Repérer un point dans un repère orthonormé';
const repereQ20 = (points) => {
    const U = 40;
    const ox = 42;
    const oy = 264;
    const X = (x) => ox + x * U;
    const Y = (y) => oy - y * U;
    const grille = 'stroke="#CBD5E1" stroke-width="1"';
    const axe = `stroke="${ENCRE}" stroke-width="1.5"`;
    const nombre = `font-size="13" fill="${ENCRE}"`;
    const lettre = `font-family="${POLICE_MATH}" font-style="italic" font-size="17" fill="${ENCRE}"`;
    let s = ouvrirSvg(300, 324, 'Repère avec quatre points A, B, C et D');
    for (let i = 1; i <= 5; i += 1) s += segment({ x: X(i), y: Y(-1.3) }, { x: X(i), y: Y(5.7) }, grille);
    [-1, 1, 2, 3, 4, 5].forEach((j) => { s += segment({ x: X(-0.6), y: Y(j) }, { x: X(5.7), y: Y(j) }, grille); });
    s += segment({ x: X(-0.6), y: oy }, { x: X(5.8), y: oy }, axe);
    s += `<polygon points="${px(X(6))},${oy} ${px(X(5.8))},${oy - 4} ${px(X(5.8))},${oy + 4}" fill="${ENCRE}"/>`;
    s += segment({ x: ox, y: Y(-1.3) }, { x: ox, y: Y(5.8) }, axe);
    s += `<polygon points="${ox},${px(Y(6))} ${ox - 4},${px(Y(5.8))} ${ox + 4},${px(Y(5.8))}" fill="${ENCRE}"/>`;
    for (let i = 1; i <= 5; i += 1) s += ecrire(X(i), oy + 17, String(i), `text-anchor="middle" ${nombre}`);
    [-1, 1, 2, 3, 4, 5].forEach((j) => { s += ecrire(ox - 8, Y(j), relatif(j), `text-anchor="end" dy=".35em" ${nombre}`); });
    s += ecrire(ox - 8, oy + 17, '0', `text-anchor="end" ${nombre}`);
    s += ecrire(X(5.85), oy + 22, 'x', `text-anchor="middle" ${lettre}`);
    s += ecrire(ox - 16, Y(5.85), 'y', `text-anchor="middle" ${lettre}`);
    points.forEach((pt) => {
        const cx = X(pt.x);
        const cy = Y(pt.y);
        const croix = `stroke="${BLEU}" stroke-width="2"`;
        s += segment({ x: cx - 5, y: cy - 5 }, { x: cx + 5, y: cy + 5 }, croix) + segment({ x: cx - 5, y: cy + 5 }, { x: cx + 5, y: cy - 5 }, croix);
        s += ecrire(cx + 7, cy - 8, pt.nom, `font-size="16" font-weight="600" fill="${BLEU}"`);
    });
    return `${s}</svg>`;
};
const genQ20 = () => {
    const y0 = randomChoice([3, 4, 5]);
    const x0 = randomChoice([1, 2, 3, 4, 5].filter((v) => v !== y0));
    const lu = x0 + y0 / 10;
    const points = shuffle([
        { role: 'juste', x: x0, y: y0 },
        { role: 'echange', x: y0, y: x0 },
        { role: 'decimal_sur_abscisses', x: lu, y: 0 },
        { role: 'decimal_sur_ordonnees', x: 0, y: lu },
    ]).map((pt, i) => ({ ...pt, nom: LETTRES[i] }));
    // second calcul : un seul des quatre points a pour abscisse x0 et pour ordonnée y0
    const trouves = points.filter((pt) => pt.x === x0 && pt.y === y0);
    garde(trouves.length === 1 && trouves[0].role === 'juste', 'Q20, point unique');
    const choix = choixDistincts(['le point A', 'le point B', 'le point C', 'le point D'], 'Q20');
    return qcmDansLOrdre(choix, `le point ${trouves[0].nom}`,
        '<p>On considère quatre points A, B, C et D dans le plan rapporté au repère ci-dessous :</p>{{element:0}}'
        + `<p>Quel point a pour coordonnées (${x0} ; ${y0}) ?</p>`,
        { ...opts(L20), elements_additionnels: figure(repereQ20(points), 'Repère avec quatre points A, B, C et D.', 'small') });
};

// ─── Q21 ────────────────────────────────────────────────────────────────────

/**
 * Q21 : Calculer l'aire d'un triangle.
 * Item : « On considère le triangle ABC représenté ci-dessous : Quel calcul donne son aire ? », triangle
 *   ABC, AB = 13, BC = 15, AC = 14, hauteur issue de B = 12 (pied sur [AC], angle droit marqué) ;
 *   (14 × 12)/2 (juste), 14 × 12, (15 × 12)/2, 13 × 14 × 15.
 * Domaine : des triangles aux trois côtés et à la hauteur entiers, comme 13, 14, 15 et 12 : deux triangles
 *   rectangles de Pythagore accolés le long de la hauteur (5, 12, 13 et 9, 12, 15 pour l'item), pied de la
 *   hauteur entre A et C, côtés distincts, jamais rectangles ; 13 triangles et leurs symétriques. Figure
 *   à l'échelle.
 * Erreurs : base × hauteur sans diviser par 2 (14 × 12) ; la hauteur avec un côté qui n'est pas sa base,
 *   le plus long des deux autres côtés comme dans l'item ((15 × 12)/2) ; le produit des trois côtés, dans
 *   l'ordre croissant (13 × 14 × 15).
 */
const L21 = 'Calculer l\'aire d\'un triangle';
const TRIANGLES_Q21 = [
    [5, 9, 12], [5, 16, 12], [6, 15, 8], [8, 20, 15], [15, 21, 20], [7, 10, 24], [10, 18, 24],
    [7, 32, 24], [12, 30, 16], [10, 32, 24], [15, 27, 36], [5, 35, 12], [20, 28, 21],
];
/** Le triangle ABC à l'échelle : A et C sur la base, B au-dessus du pied H de la hauteur. */
const triangleABC = (ah, hc, h, ab, bc) => {
    const ac = ah + hc;
    const e = Math.min(210 / ac, 140 / h);
    const oy = px(30 + h * e);
    const A = { x: 46, y: oy };
    const C = { x: px(46 + ac * e), y: oy };
    const H = { x: px(46 + ah * e), y: oy };
    const B = { x: H.x, y: px(oy - h * e) };
    const G = { x: (A.x + B.x + C.x) / 3, y: (A.y + B.y + C.y) / 3 };
    const police = `font-size="15" fill="${ENCRE}"`;
    const sommet = `font-size="17" font-weight="600" fill="${ENCRE}"`;
    // la longueur d'un côté, au milieu du côté, à l'extérieur du triangle
    const cote = (P, Q, valeur) => {
        const M = { x: (P.x + Q.x) / 2, y: (P.y + Q.y) / 2 };
        const l = Math.hypot(Q.x - P.x, Q.y - P.y);
        const n = { x: (Q.y - P.y) / l, y: -(Q.x - P.x) / l };
        const sens = n.x * (M.x - G.x) + n.y * (M.y - G.y) < 0 ? -1 : 1;
        return ecrire(M.x + 16 * sens * n.x, M.y + 16 * sens * n.y, String(valeur), `text-anchor="middle" dy=".35em" ${police}`);
    };
    const yb = oy + 20;
    let s = ouvrirSvg(300, px(yb + 30), 'Triangle ABC avec les longueurs de ses côtés et une hauteur');
    s += `<polygon points="${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}" fill="#E5E7EB" ${TRAIT} stroke-linejoin="round"/>`;
    s += segment(B, H, `stroke="${ENCRE}" stroke-width="1.3"`);
    s += `<polyline points="${px(H.x - 10)},${H.y} ${px(H.x - 10)},${px(H.y - 10)} ${H.x},${px(H.y - 10)}" fill="none" stroke="${ENCRE}" stroke-width="1.2"/>`;
    s += cote(A, B, ab) + cote(B, C, bc);
    // la hauteur, écrite du côté le plus large, aux deux cinquièmes de sa longueur depuis la base
    const yh = H.y - 0.4 * h * e;
    s += hc >= ah
        ? ecrire(H.x + 7, yh, String(h), `dy=".35em" ${police}`)
        : ecrire(H.x - 7, yh, String(h), `text-anchor="end" dy=".35em" ${police}`);
    // la base AC, cotée par une double flèche
    s += segment({ x: A.x + 4, y: yb }, { x: C.x - 4, y: yb }, `stroke="${ENCRE}" stroke-width="1.3"`);
    s += `<polygon points="${A.x},${yb} ${A.x + 8},${yb - 4} ${A.x + 8},${yb + 4}" fill="${ENCRE}"/>`;
    s += `<polygon points="${C.x},${yb} ${px(C.x - 8)},${yb - 4} ${px(C.x - 8)},${yb + 4}" fill="${ENCRE}"/>`;
    s += ecrire((A.x + C.x) / 2, yb + 19, String(ac), `text-anchor="middle" ${police}`);
    s += ecrire(A.x - 8, A.y + 5, 'A', `text-anchor="end" ${sommet}`);
    s += ecrire(C.x + 8, C.y + 5, 'C', sommet);
    s += ecrire(B.x, B.y - 9, 'B', `text-anchor="middle" ${sommet}`);
    return `${s}</svg>`;
};
const genQ21 = () => {
    const [p1, p2, h] = randomChoice(TRIANGLES_Q21);
    const [ah, hc] = Math.random() < 0.5 ? [p1, p2] : [p2, p1];
    const ab = Math.sqrt(ah * ah + h * h);
    const bc = Math.sqrt(hc * hc + h * h);
    const ac = ah + hc;
    // second calcul : côtés entiers (Pythagore dans ABH et CBH), distincts, triangle non rectangle, et
    // l'aire de Héron égale à base × hauteur ÷ 2 : (a+b+c)(−a+b+c)(a−b+c)(a+b−c) = 16 S² = 4 (AC × BH)²
    const heron = (ab + bc + ac) * (-ab + bc + ac) * (ab - bc + ac) * (ab + bc - ac);
    garde(Number.isInteger(ab) && Number.isInteger(bc), 'Q21, côtés entiers');
    garde(new Set([ab, bc, ac]).size === 3 && ab * ab + bc * bc !== ac * ac, 'Q21, triangle');
    garde(heron === 4 * (ac * h) ** 2, 'Q21, aire');
    const autreCote = Math.max(ab, bc);
    const [c1, c2, c3] = [ab, bc, ac].sort((x, y) => x - y);
    const juste = tex(`\\dfrac{${ac} \\times ${h}}{2}`);
    const choix = choixDistincts([juste, tex(`${ac} \\times ${h}`), tex(`\\dfrac{${autreCote} \\times ${h}}{2}`), tex(`${c1} \\times ${c2} \\times ${c3}`)], 'Q21');
    return makeQcm(choix, juste, '<p>On considère le triangle ABC représenté ci-dessous :</p>{{element:0}}<p>Quel calcul donne son aire ?</p>',
        { ...opts(L21), elements_additionnels: figure(triangleABC(ah, hc, h, ab, bc), 'Triangle ABC avec les longueurs de ses côtés et une hauteur.', 'small') });
};

// ─── Q22 ────────────────────────────────────────────────────────────────────

/**
 * Q22 : Encadrer un nombre décimal entre deux nombres entiers consécutifs.
 * Item : « Quel encadrement de 56/10 par deux nombres entiers est correct ? » ; 5 < 56/10 < 6 (juste),
 *   55 < 56/10 < 57, 4 < 56/10 < 5, 0 < 56/10 < 1.
 * Domaine : n/10, n de 21 à 99, jamais un multiple de 10 ; partie entière q de 2 à 9 (pour q = 1,
 *   « q − 1 < … < q » serait « 0 < … < 1 »).
 * Erreurs : le numérateur encadré, la barre de fraction ignorée (55 < … < 57) ; une fraction prise pour
 *   un nombre plus petit que 1 (0 < … < 1) ; 4 < … < 5 : la partie entière prise comme borne supérieure,
 *   l'encadrement décalé d'une unité vers le bas. Erreur non identifiée avec certitude, TRANCHÉ LE 10/10 (on garde) ; la
 *   variante reproduit sa forme, q − 1 < n/10 < q.
 */
const L22 = 'Encadrer un nombre décimal entre deux entiers consécutifs';
const genQ22 = () => {
    const q = randInt(2, 9);
    const n = 10 * q + randInt(1, 9);
    // second calcul : q × 10 < n < (q + 1) × 10, donc q < n/10 < q + 1
    garde(10 * q < n && n < 10 * (q + 1), 'Q22, encadrement');
    const encadrement = (x, y) => tex(`${x} \\lt \\dfrac{${n}}{10} \\lt ${y}`);
    const juste = encadrement(q, q + 1);
    const choix = choixDistincts([juste, encadrement(n - 1, n + 1), encadrement(q - 1, q), encadrement(0, 1)], 'Q22');
    return makeQcm(choix, juste, `<p>Quel encadrement de ${fracTex(n, 10)} par deux nombres entiers est correct ?</p>`, opts(L22));
};

// ─── Registre ───────────────────────────────────────────────────────────────

const GENERATORS = {
    q01_qcm_unique: genQ01,
    q02_qcm_unique: genQ02,
    q03_qcm_unique: genQ03,
    q04_qcm_unique: genQ04,
    q05_qcm_unique: genQ05,
    q06_qcm_unique: genQ06,
    q07_qcm_unique: genQ07,
    q08_qcm_unique: genQ08,
    q09_qcm_unique: genQ09,
    q10_qcm_unique: genQ10,
    q11_qcm_unique: genQ11,
    q12_qcm_unique: genQ12,
    q13_qcm_unique: genQ13,
    q14_qcm_unique: genQ14,
    q15_qcm_unique: genQ15,
    q16_qcm_unique: genQ16,
    q17_qcm_unique: genQ17,
    q18_qcm_unique: genQ18,
    q19_qcm_unique: genQ19,
    q20_qcm_unique: genQ20,
    q21_qcm_unique: genQ21,
    q22_qcm_unique: genQ22,
};

export const ITEMS = [
    { questionNum: 'Q01', formatKey: 'q01_qcm_unique', libelle: L01, bonneReponseOfficielle: '10 fois plus petit.' },
    { questionNum: 'Q02', formatKey: 'q02_qcm_unique', libelle: L02, bonneReponseOfficielle: '35' },
    { questionNum: 'Q03', formatKey: 'q03_qcm_unique', libelle: L03, bonneReponseOfficielle: '5/8' },
    { questionNum: 'Q04', formatKey: 'q04_qcm_unique', libelle: L04, bonneReponseOfficielle: '186' },
    { questionNum: 'Q05', formatKey: 'q05_qcm_unique', libelle: L05, bonneReponseOfficielle: '2' },
    { questionNum: 'Q06', formatKey: 'q06_qcm_unique', libelle: L06, bonneReponseOfficielle: 'a² + 3a + 7' },
    { questionNum: 'Q07', formatKey: 'q07_qcm_unique', libelle: L07, bonneReponseOfficielle: '2h 15 min' },
    { questionNum: 'Q08', formatKey: 'q08_qcm_unique', libelle: L08, bonneReponseOfficielle: '120' },
    { questionNum: 'Q09', formatKey: 'q09_qcm_unique', libelle: L09, bonneReponseOfficielle: '110°' },
    { questionNum: 'Q10', formatKey: 'q10_qcm_unique', libelle: L10, bonneReponseOfficielle: '14,602' },
    { questionNum: 'Q11', formatKey: 'q11_qcm_unique', libelle: L11, bonneReponseOfficielle: '9 km' },
    { questionNum: 'Q12', formatKey: 'q12_qcm_unique', libelle: L12, bonneReponseOfficielle: '4 300' },
    { questionNum: 'Q13', formatKey: 'q13_qcm_unique', libelle: L13, bonneReponseOfficielle: 'Image DD' },
    { questionNum: 'Q14', formatKey: 'q14_qcm_unique', libelle: L14, bonneReponseOfficielle: '12' },
    { questionNum: 'Q15', formatKey: 'q15_qcm_unique', libelle: L15, bonneReponseOfficielle: '3/4' },
    { questionNum: 'Q16', formatKey: 'q16_qcm_unique', libelle: L16, bonneReponseOfficielle: '3/10' },
    { questionNum: 'Q17', formatKey: 'q17_qcm_unique', libelle: L17, bonneReponseOfficielle: '25' },
    { questionNum: 'Q18', formatKey: 'q18_qcm_unique', libelle: L18, bonneReponseOfficielle: '7 500' },
    { questionNum: 'Q19', formatKey: 'q19_qcm_unique', libelle: L19, bonneReponseOfficielle: '27' },
    { questionNum: 'Q20', formatKey: 'q20_qcm_unique', libelle: L20, bonneReponseOfficielle: 'le point D' },
    { questionNum: 'Q21', formatKey: 'q21_qcm_unique', libelle: L21, bonneReponseOfficielle: '(14 × 12)/2' },
    { questionNum: 'Q22', formatKey: 'q22_qcm_unique', libelle: L22, bonneReponseOfficielle: '5 < 56/10 < 6' },
];

export const CATEGORIES = ITEMS.map(({ questionNum, formatKey, libelle }) => ({
    id: questionNum.toLowerCase(),
    label: `${questionNum} · ${libelle}`,
    formatsList: [{ key: formatKey, label: 'QCM' }],
}));

export const generate = (typeExercice) => {
    const key = GENERATORS[typeExercice] ? typeExercice : randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
